import React, { useState, useEffect, useCallback } from "react";
import {
  Accordion,
  AccordionItem,
  Button,
  CheckboxGroup,
  FormField,
  Rows,
  SegmentedControl,
  Select,
  Text,
} from "@canva/app-ui-kit";
import {
  CHART_OPTIONS,
  HISTORY_MONTH_OPTIONS,
  METRIC_OPTIONS,
  type ChartKey,
  type MetricKey,
} from "./slide-options";

export interface BoardDeckConfig {
  metrics: MetricKey[];
  chartType: ChartKey;
  historyMonths: number;
}

export const DEFAULT_BOARD_DECK_CONFIG: BoardDeckConfig = {
  metrics: ["arr", "burnRate", "cashBalance", "runway"],
  chartType: "revenue",
  historyMonths: 12,
};

const STORAGE_KEY_PREFIX = "profitual:board-deck-config:";

function loadConfig(scenarioId: string): BoardDeckConfig {
  try {
    const raw = localStorage.getItem(STORAGE_KEY_PREFIX + scenarioId);
    if (!raw) return DEFAULT_BOARD_DECK_CONFIG;
    return { ...DEFAULT_BOARD_DECK_CONFIG, ...JSON.parse(raw) };
  } catch {
    return DEFAULT_BOARD_DECK_CONFIG;
  }
}

function saveConfig(scenarioId: string, config: BoardDeckConfig) {
  try {
    localStorage.setItem(STORAGE_KEY_PREFIX + scenarioId, JSON.stringify(config));
  } catch {
    // Non-fatal in iframe sandbox
  }
}

interface Props {
  scenarioId: string;
  onInsert: (config: BoardDeckConfig) => Promise<void>;
  loading?: boolean;
  error?: string | null;
}

export function BoardDeckConfigCard({ scenarioId, onInsert, loading, error }: Props) {
  const [config, setConfig] = useState<BoardDeckConfig>(() => loadConfig(scenarioId));

  useEffect(() => setConfig(loadConfig(scenarioId)), [scenarioId]);
  useEffect(() => saveConfig(scenarioId, config), [scenarioId, config]);

  const update = useCallback(
    <K extends keyof BoardDeckConfig>(key: K, value: BoardDeckConfig[K]) => {
      setConfig((prev) => ({ ...prev, [key]: value }));
    },
    []
  );

  const kpiWarning =
    config.metrics.length === 0
      ? "Pick at least one KPI"
      : config.metrics.length > 4
        ? "Board Deck fits 4 KPIs — the first 4 will be used"
        : null;

  return (
    <Rows spacing="1.5u">
      <FormField
          label="Chart"
          value={config.chartType}
          control={(props) => (
            <Select<ChartKey>
              {...props}
              options={CHART_OPTIONS.map((o) => ({ value: o.value, label: o.label }))}
              onChange={(v) => update("chartType", v)}
              stretch
            />
          )}
        />

        <FormField
          label="Chart history"
          value={String(config.historyMonths)}
          control={(props) => (
            <SegmentedControl
              {...props}
              options={HISTORY_MONTH_OPTIONS.map((o) => ({ value: o.value, label: o.label }))}
              onChange={(v) => update("historyMonths", Number(v))}
            />
          )}
        />

        <Accordion>
          <AccordionItem title="KPIs">
            <Rows spacing="0.5u">
              <CheckboxGroup
                options={METRIC_OPTIONS.map((o) => ({ value: o.value, label: o.label }))}
                value={config.metrics}
                onChange={(v) => update("metrics", v as MetricKey[])}
              />
              {kpiWarning && (
                <Text size="small" tone="tertiary">
                  {kpiWarning}
                </Text>
              )}
            </Rows>
          </AccordionItem>
        </Accordion>

        {error && (
          <Text size="small" tone="critical">
            {error}
          </Text>
        )}

        <Button
          variant="secondary"
          onClick={() => onInsert(config)}
          loading={loading}
          disabled={config.metrics.length === 0}
          stretch
        >
          Insert Board Deck
      </Button>
    </Rows>
  );
}
